'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { Listing } from '@repo/api';
import { Skeleton } from '@/components/ui/Skeleton';

const UnitMap = dynamic(() => import('./UnitMap').then(m => m.UnitMap), {
  ssr: false,
  loading: () => <Skeleton className="h-64 w-full rounded-2xl" />,
});

export function UnitMapLazy({ listing }: { listing: Listing }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldMountMap, setShouldMountMap] = useState(false);

  useEffect(() => {
    if (shouldMountMap) return;

    const el = containerRef.current;
    if (!el) return;

    // Fallback for older browsers: mount immediately.
    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      setShouldMountMap(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) return;
        setShouldMountMap(true);
        observer.disconnect();
      },
      {
        // Start loading slightly before the section is visible.
        rootMargin: '300px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldMountMap]);

  return (
    <div ref={containerRef}>
      {shouldMountMap ? (
        <UnitMap listing={listing} />
      ) : (
        <Skeleton className="h-64 w-full rounded-2xl" />
      )}
    </div>
  );
}
